import {
  ChangeDetectorRef,
  Component, DestroyRef, EventEmitter,
  Input,
  OnDestroy,
  OnInit, Output,
  Renderer2
} from '@angular/core';
import {DragulaService} from "ng2-dragula";
import {DragHelperService} from "../../../../services/drag-helper.service";
import {CanvasZoomService} from "../../../../services/canvas-zoom.service";
import {
  BuilderGeneratedPageSectionI,
  PageMetaI,
  SecondaryPageReqI, SecondaryPageTypeReqI,
  SecondaryPageWithSectionsI,
  SectionTemplateI
} from "../../../../builder-v2.interface";
import {BuilderV2Service} from "../../../../builder-v2.service";
import {CommonBuilderDataService} from "../../../../services/common-builder-data.service";
import {BuilderSectionsInteractionService} from "../../../../builder-sections-interaction.service";
import {filter, map} from "rxjs/operators";
import {combineLatest} from "rxjs/internal/observable/combineLatest";
import {skip} from "rxjs/internal/operators/skip";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {BuilderHistoryService} from "../../../../builder-history.service";
import * as _ from 'lodash';
import {TriggersService} from "../../../../services/triggers.service";
import {CanvasPageInteractionsService} from "../../../../services/canvas-page-interactions.service";
import {BuilderStepsEnum} from "../../../../builder-tour.enum";
import {SinglePageService} from "../../../../single-page/services/single-page.service";
import {NotifierComponent} from "../../../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../../../shared/components/notifier/notification";
import {NotificationType} from "../../../../../shared/components/notifier/notification-type";

@Component({
    selector: 'db-secondary-pages',
    templateUrl: './secondary-pages.component.html',
    styleUrl: './secondary-pages.component.scss',
    standalone: false
})
export class SecondaryPagesComponent implements OnInit, OnDestroy {
  @Input('pageMeta') set pageList(list) {
    if (!list) {
      return;
    }
    const [first, ...rest] = list;

    if(first?.id && !rest?.at(0) && this._commonDataService.combinedOutline){
      this.pageMeta = []
      this.alreadyHasSections = true;
    }
    if (!rest?.at(0)) {
      return;
    }
    this.pageMeta = rest;
    this.alreadyHasSections = rest.length && !!rest[0].sections?.length;
  }

  @Input('pagesLoading') set pageLoaded(value: boolean) {
    this.pageIsLoading = value;
    if (this.pageIsLoading && this.isSinglePage) {
      this.pagesWithSections = [];
    }
    if (!value) {
      if (this.alreadyHasSections) {
        this._setPagesFromLocalStorage();
      } else {
        this._getSecondaryPages();
      }
    }
  }

  @Input() isSinglePage: boolean = false;

  pageIsLoading = true;

  alreadyHasSections = false;
  pageMeta: PageMetaI[] = [];
  @Input() sectionTemplates: SectionTemplateI[] = [];
  pagesWithSections: SecondaryPageWithSectionsI[] = [];
  selectedPageId = null;
  addPageTooltipOpened = false;
  pagesLimit = this._commonDataService.secondaryPagesLimit;
  newPageLoading = false;
  loadingPageIds = null;
  pageRegenerating = null;
  isMobile = window.innerWidth <= 991;
  regenPageId: number = null;
  @Output() openMagicLink: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(private _dragulaService: DragulaService,
              private _canvasZoomService: CanvasZoomService,
              private _dragHelperService: DragHelperService,
              private _builderService: BuilderV2Service,
              private _renderer: Renderer2,
              public _commonDataService: CommonBuilderDataService,
              private _builderInteractionsService: BuilderSectionsInteractionService,
              private builderHistoryService: BuilderHistoryService,
              private _pageInteractionsService: CanvasPageInteractionsService,
              private _destroyRef: DestroyRef,
              private _triggerService: TriggersService,
              private _singlePageService: SinglePageService,
              private cdr: ChangeDetectorRef) {
    this._dragulaService.createGroup('secondaryPages', {
      revertOnSpill: true,
      direction: 'horizontal',
      moves: (el, container, handle) => {
        if (this.isSinglePage) {
          return;
        }
        return handle.classList.contains('page-handle');
      }
    });
    this._dragulaService.shadow('secondaryPages')
      .subscribe(value => {
        this._getPagesSortedIds();
      });

  }

  getSectionsByPageId(id: number): SecondaryPageWithSectionsI {
    return this.pagesWithSections.find(page => page.page.id === id);
  }

  ngOnInit() {
    this._listenStateChange();
    this._listenAddNewPage();
    this.pagesLimit = this.isSinglePage ? 7 : this._commonDataService.secondaryPagesLimit;
  }

  private _listenAddNewPage() {
    this._pageInteractionsService.newPageTitle
      .pipe(
        takeUntilDestroyed(this._destroyRef),
      ).subscribe(value => {
      this.addPageTooltipOpened = false;
      this.addPage(value, this._commonDataService.websiteType === 'ecommerce');
    });
  }

  private _listenStateChange() {
    this.builderHistoryService.currentStateIndex
      .pipe(
        skip(1),
        takeUntilDestroyed(this._destroyRef),
      ).subscribe(index => {
      let history = this.builderHistoryService.history.value[index];
      if (history && history.secondaryPages) {
        let {secondaryPages} = history;
        secondaryPages = _.cloneDeep(secondaryPages);
        this.pagesWithSections = secondaryPages;
        this.pageMeta = secondaryPages.map(secondary => secondary.page);
        this._updateSecondaryPagesSubject();
        this._builderInteractionsService.saveLocalState.next();
        this.cdr.detectChanges();
      }
    });
  }

  private _setPagesFromLocalStorage() {
    const pagesWithSections = this.pageMeta.map((resp, index) => {
      return {
        page: this.pageMeta[index],
        sections: resp.sections
      };
    });
    // if (!pagesWithSections.length) {
    //   this._builderInteractionsService.secondaryPagesLoaded.next(true);
    // }
    this._setPagesWithSections(pagesWithSections);
  }

  private _getSecondaryPages() {
    const pagesRequests = this.pageMeta.map(page => this._getPage(page));
    if (!pagesRequests.length && this.builderHistoryService.builderStep.value === BuilderStepsEnum.SHOW_CANVAS) {
      setTimeout(() => {
        this._builderInteractionsService.secondaryPagesLoaded.next(true);
      });
    }
    combineLatest(pagesRequests).pipe(
      filter(value => !!value?.every(resp => !!resp?.data)),
      map(value => value.map((resp, index) => {
        if (this.pageMeta[index]) {
          this.pageMeta[index].page_type = resp.data.page_type;
        }
        return {
          page: this.pageMeta[index],
          sections: this._setEmptyPagesWithHeaderAndFooter(resp.data.page_outline.sections)
        };
      }))
    ).subscribe(value => {
      this._setPagesWithSections(value);
    });
  }

  private _setEmptyPagesWithHeaderAndFooter(sections: BuilderGeneratedPageSectionI[]) {
    if (!sections.length) {
      sections = [
        {
          section_title: 'Header',
          section_description: 'header',
          section_type: 'header'
        },
        {
          section_title: 'Footer',
          section_description: 'footer',
          section_type: 'footer'
        }
      ];
    }
    return sections;
  }

  private _setPagesWithSections(value: SecondaryPageWithSectionsI[]) {
    this.pagesWithSections = value;
    this.saveInHistory(true);
    this._updateSecondaryPagesSubject();
    this._builderInteractionsService.saveLocalState.next();
    // this._getPageType();
    this.cdr.markForCheck();
    this._recalculateMinZoom();
    setTimeout(() => {
      this._builderInteractionsService.secondaryPagesLoaded.next(true);
    }, 3000);
  }

  private _recalculateMinZoom() {
    setTimeout(() => {
      this._triggerService.calculateMinZoom.next();
    }, 1000);
  }

  private _getPage(page) {
    const data = this._getPagesRequestData(page);
    const {domainId, uniqueId} = this._commonDataService;
    this._builderInteractionsService.secondaryPageLoading.next(true);
    return this._builderService.getSecondaryPage(data, domainId, uniqueId);
  }

  private _getPagesRequestData(page: PageMetaI): SecondaryPageReqI {
    const {business_type, business_name, business_description} = this._builderInteractionsService.siteInfo.value;
    const sections = this._builderInteractionsService.removeFormControls(this._builderInteractionsService.pageSections
      .filter(section => section.section_title || section.section_description)
      .map(({loading, is_tempo, ...section}, index) => {
        return {...section, index};
      }));

    return {
      business_type,
      business_name,
      business_description,
      website_type: this._commonDataService.websiteType,
      ecom_data: this._builderInteractionsService.updateProductCategories.value,
      page_description: page.description,
      page_index: page.index,
      page_title: page.title,
      page_type: page.page_type,
      slug: page.slug,
      id: page.id,
      homepage_outline: {sections}
    };
  }

  onStructureChange(data: any[]) {
    this._builderInteractionsService.saveLocalState.next();
    this.saveInHistory();
  }

  private saveInHistory(isFirst = false) {
    const pagesWithSections = this.pagesWithSections.map(({page, sections}) => {
      if (!sections.length && this.newPageLoading && page.id === this.pageRegenerating?.page?.id) {
        sections = this.pageRegenerating.sections;
      }
      sections = sections.map(section => {
        section.section_description = section.description_control?.value || section.section_description;
        section.section_title = section.title_control?.value || section.section_title;
        return section;
      });
      return {
        page,
        sections: structuredClone(this._builderInteractionsService.removeExtraKeysFromSections(sections))
      };
    });
    this.builderHistoryService.addSecondaryPageInHistory(_.cloneDeep(pagesWithSections), isFirst);
  }

  resetSelect() {
    this.selectedPageId = null;
  }

  dragClassChanged() {
    this._dragHelperService.recalculateDraggableElementPosition(this._canvasZoomService.scale.value, this._renderer, true);
  }

  private _updateIndex(data) {
    this.pageMeta = data.map((page, index) => {
      page.index = index + 1;
      return page;
    });
    this.cdr.detectChanges();
  }

  private _getPagesSortedIds() {
    this._changePageStructure();
    this.pagesWithSections = this.pagesWithSections.sort((a, b) => {
      return this.pageMeta.findIndex(page => page.id === a.page.id) - this.pageMeta.findIndex(page => page.id === b.page.id);
    });
    this._updateSecondaryPagesSubject();
  }

  private _changePageStructure() {
    let containers = document.querySelectorAll('.secondary-pages-list-container');
    let childIds = [];

    containers.forEach(container => {
      let children = container.children;

      Array.from(children).forEach(child => {
        if (child.id) {
          childIds.push(+child.id);
        }
      });
    });

    let sortedObjectsArray = this.pageMeta.sort((a, b) => {
      return childIds.indexOf(a.id) - childIds.indexOf(b.id);
    });
    this._updateIndex(sortedObjectsArray);
  }

  addPage(name, shouldRegenerate = false) {
    this.newPageLoading = true;
    const indexToAdd = this.pageMeta.length;
    const page: PageMetaI = {
      id: new Date().getTime(),
      index: indexToAdd + 1,
      page_type: null,
      title: name,
      description: '',
      slug: null
    };
    this.pageMeta.splice(indexToAdd, 0, page);
    this.pagesWithSections.splice(indexToAdd, 0, {
      page,
      sections: []
    });
    this.pagesWithSections[indexToAdd].sections = [];
    this.cdr.detectChanges();
    this._triggerService.scrollToSection.next(page.id.toString());

    if (shouldRegenerate) {
      this.regenPageId = page.id;
      return;
    }

    this._getPage(page)
      .subscribe(resp => {
        if (resp?.data) {
          const {id, slug, page_description, title, page_type, index} = resp.data;
          let newPageIndex = this.pageMeta.findIndex(p => p.id === id);
          this.pageMeta[newPageIndex] = {
            id,
            slug,
            description: page_description,
            title: page.title,
            page_type,
            index: newPageIndex + 1
          };
          this.pagesWithSections[newPageIndex] = {
            page: this.pageMeta[newPageIndex],
            sections: resp.data.page_outline.sections
          };
          this._updateSecondaryPagesSubject();
          this._builderInteractionsService.saveLocalState.next();
          this.newPageLoading = false;
          this.saveInHistory();
          this.cdr.detectChanges();
          this._getPagesSortedIds();
        } else if (resp && !resp.success) {
          this.newPageLoading = false;
          NotifierComponent.notify(new Notification("Something went wrong...", NotificationType.DANGER));
        }
      }, error => {
        console.log(error);
      });
  }

  deletePage(pageId: number) {
    let pageIndex = this.pageMeta.findIndex(p => p.id === pageId);
    this.pageMeta.splice(pageIndex, 1);
    this.pagesWithSections.splice(pageIndex, 1);
    this._updateSecondaryPagesSubject();
    this._builderInteractionsService.saveLocalState.next();
    this._updateIndex(this.pageMeta);
    this.saveInHistory();
  }

  regeneratePage(page: PageMetaI) {
    if (page.id === this.regenPageId) {
      this.regenPageId = null;
    }
    let pageIndex = this.pageMeta.findIndex(p => p.id === page.id);
    page.index = pageIndex + 1;
    this.pageMeta[pageIndex] = page;
    this.pageRegenerating = _.cloneDeep(this.pagesWithSections[pageIndex]);
    this.pagesWithSections[pageIndex] = {
      page,
      sections: []
    };
    this.cdr.detectChanges();
    this.newPageLoading = true;
    this._getPage(page).subscribe(resp => {
      if (resp?.data) {
        let newPageIndex = this.pageMeta.findIndex(p => p.id === page.id);
        this.pageMeta[newPageIndex] = page;
        this.pagesWithSections[newPageIndex] = {
          page: this.pageMeta[newPageIndex],
          sections: this._setEmptyPagesWithHeaderAndFooter(resp.data.page_outline.sections)
        };
        this._updateSecondaryPagesSubject();
        this.loadingPageIds = page.id;
        this._updateIndex(this.pageMeta);
        this.saveInHistory();
        this.newPageLoading = false;

        setTimeout(() => {
          this.loadingPageIds = null;
        });
      } else if (resp && !resp?.success) {
        this.newPageLoading = false;
        this.loadingPageIds = null;
        NotifierComponent.notify(new Notification("Something went wrong...", NotificationType.DANGER));
      }
    });
  }

  onSectionTypeChange(index: number, sections: BuilderGeneratedPageSectionI[]) {
    this.pagesWithSections[index].sections = sections;
    this._updateSecondaryPagesSubject();
    this._builderInteractionsService.saveLocalState.next();
  }

  onSectionListChange(index: number, sections: BuilderGeneratedPageSectionI[], pageId: number) {
    const realIndex = this.pagesWithSections.findIndex(data => data.page.id === pageId);
    this.pagesWithSections[realIndex].sections = sections;
    this._updateSecondaryPagesSubject();
    this._builderInteractionsService.saveLocalState.next();
    this.saveInHistory();
  }

  onPageChange(index: number, page: PageMetaI) {
    const realIndex = this.pagesWithSections.findIndex(data => data.page.id === page.id);
    this.pagesWithSections[realIndex].page = {...page, index: this.pagesWithSections[realIndex].page.index};
    this._updateSecondaryPagesSubject();
    this._builderInteractionsService.saveLocalState.next();
    this.saveInHistory();
  }

  private _getPageType(index?, page?) {
    const data = this._getPagesTypeRequestData(index, page);
    this._builderService.getSecondaryPageType(data, this._commonDataService.domainId, this._commonDataService.uniqueId)
      .subscribe(value => {
        if (value?.data) {
          value.data.map((page: PageMetaI) => {
            const index = this.pageMeta.findIndex((p: PageMetaI) => p.index === page.index);
            this.pageMeta[index] = page;
            this.pagesWithSections[index] = {...this.pagesWithSections[index], page};
          });
          this._updateSecondaryPagesSubject();
          this._builderInteractionsService.saveLocalState.next();
          this.cdr.detectChanges();
        }
      });
  }

  private _getPagesTypeRequestData(index = null, page = null): SecondaryPageTypeReqI {
    const {business_type, business_name, business_description} = this._builderInteractionsService.siteInfo.value;

    let meta = this._builderInteractionsService.getPagesMeta();
    if (page) {
      meta.splice(index, 0, page);
      meta = meta.map((page, index) => {
        page.index = index;
        return page;
      });
    }

    return {
      business_type,
      business_name,
      business_description,
      website_type: this._commonDataService.websiteType,
      page_index: index,
      pages_meta: meta
    };
  }

  selectPage(pageId: number) {
    this.selectedPageId = pageId;
  }

  private _updateSecondaryPagesSubject() {
    this._builderInteractionsService.secondaryPages.next([...this.pagesWithSections]);
    if (this.isSinglePage) {
      const value = {
        ...this._singlePageService.page.value,
        sections: _.cloneDeep(this.pagesWithSections.at(0).sections)
      };
      this._singlePageService.page.next(value);
    }
  }

  openAddPage() {
    if (this.isMobile) {
      this.openMagicLink.emit(true);
      return;
    }
    this._pageInteractionsService.openAddPage.next({pageId: 5555});
  }

  ngOnDestroy() {
    this._dragulaService.destroy('secondaryPages');
  }

}
